import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import colors, { shadows } from '../theme';
import FeedbackModal from './FeedbackModal';

function TopBar() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        const orgData = localStorage.getItem('organization');
        if (userData) {
            setUser(JSON.parse(userData));
        }
        if (orgData) {
            setOrganization(JSON.parse(orgData));
        }
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('organization');
        navigate('/login');
    };

    // Map plan types to display names
    const getPlanDisplay = (planType) => {
        const plans = {
            'free': 'FREE',
            'per_user': 'PRO',
            'enterprise': 'ENTERPRISE'
        };
        return plans[planType] || planType?.toUpperCase();
    };

    return (
        <div style={{
            height: '64px',
            backgroundColor: colors.white,
            borderBottom: `1px solid ${colors.gray[200]}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 0rem',
            position: 'fixed',
            top: 0,
            left: '70px',
            right: 0,
            zIndex: 100
        }}>
            {/* Left - Organization Info */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                flex: '1'
            }}>
                {organization && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {/* Slash */}
                        <span style={{
                            fontSize: '1.5rem',
                            fontWeight: '300',
                            color: colors.gray[300]
                        }}>
                            /
                        </span>
                        
                        {/* Organization Name */}
                        <span style={{
                            fontSize: '1rem',
                            fontWeight: '600',
                            color: colors.gray[700]
                        }}>
                            {organization.name}
                        </span>
                        
                        {/* Plan Badge */}
                        <span style={{
                            padding: '0px 10px',
                            backgroundColor: colors.gray[100],
                            color: colors.gray[700],
                            fontSize: '0.6rem',
                            fontWeight: '700',
                            borderRadius: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            border: `1px solid ${colors.gray[200]}`
                        }}>
                            {getPlanDisplay(organization.plan_type)}
                        </span>
                    </div>
                )}
            </div>

            {/* Right - Create, Feedback & Avatar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
            }}>
                <button
                    onClick={() => navigate('/create')}
                    style={{
                        padding: '6px 18px',
                        backgroundColor: colors.buttonBlue,
                        color: colors.white,
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: shadows.md,
                        transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = shadows.lg;
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = shadows.md;
                    }}
                >
                    <span style={{ fontSize: '1.05rem' }}>+</span>
                    Create
                </button>

                 {/* Feedback Button */}
                <button
                    onClick={() => setShowFeedbackModal(true)}
                    style={{
                        padding: '7px 14px',
                        backgroundColor: 'transparent',
                        color: colors.gray[600],
                        border: `1px solid ${colors.white}`,
                        borderRadius: '16px',
                        fontSize: '0.78rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = colors.gray[200];
                        e.currentTarget.style.backgroundColor = colors.gray[200];
                        e.currentTarget.style.color = colors.gray[700];
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = colors.white;
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = colors.gray[600];
                    }}
                >
                    Feedback
                </button>

                {/* Avatar Dropdown */}
                <div style={{
                    position: 'relative',
                    marginRight: '1rem'
                }}>
                    <div 
                        onClick={() => setShowDropdown(!showDropdown)}
                        style={{
                            width: '30px',
                            height: '30px',
                            backgroundColor: colors.accent3,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: `2px solid ${showDropdown ? colors.accent3 : 'transparent'}`
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = shadows.lg;
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        👤
                    </div>

                    {/* Dropdown Menu */}
                    {showDropdown && (
                        <>
                            {/* Backdrop to close dropdown when clicking outside */}
                            <div 
                                onClick={() => setShowDropdown(false)}
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 999
                                }}
                            />
                            
                            <div style={{
                                position: 'absolute',
                                top: 'calc(100% + 8px)',
                                right: 0,
                                backgroundColor: colors.white,
                                borderRadius: '8px',
                                boxShadow: shadows.lg,
                                border: `1px solid ${colors.gray[200]}`,
                                minWidth: '200px',
                                zIndex: 1000,
                                overflow: 'hidden'
                            }}>
                                {/* User Info */}
                                <div style={{
                                    padding: '16px',
                                    borderBottom: `1px solid ${colors.gray[200]}`,
                                    backgroundColor: colors.gray[50]
                                }}>
                                    <div style={{
                                        fontSize: '0.95rem',
                                        fontWeight: '600',
                                        color: colors.dark,
                                        marginBottom: '6px'
                                    }}>
                                        {user?.name}
                                    </div>
                                    <div style={{
                                        fontSize: '0.8rem',
                                        color: colors.gray[600],
                                        marginBottom: '8px'
                                    }}>
                                        {user?.email}
                                    </div>
                                    <div style={{
                                        display: 'inline-block',
                                        padding: '4px 8px',
                                        backgroundColor: colors.accent3,
                                        color: colors.white,
                                        fontSize: '0.7rem',
                                        fontWeight: '600',
                                        borderRadius: '4px',
                                        textTransform: 'uppercase'
                                    }}>
                                        {user?.role}
                                    </div>
                                </div>

                                {/* Account Settings Button */}
                                <button
                                    onClick={() => {
                                        setShowDropdown(false);
                                        alert('Account Settings page coming soon!');
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        backgroundColor: colors.white,
                                        border: 'none',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        fontSize: '0.875rem',
                                        color: colors.gray[700],
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        borderBottom: `1px solid ${colors.gray[200]}`
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.backgroundColor = colors.gray[50];
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.backgroundColor = 'white';
                                    }}
                                >
                                    <span>⚙️</span>
                                    Account Settings
                                </button>

                                {/* Logout Button */}
                                <button
                                    onClick={handleLogout}
                                    style={{
                                        width: '100%',
                                        padding: '12px 16px',
                                        backgroundColor: colors.white,
                                        border: 'none',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        fontSize: '0.875rem',
                                        color: colors.gray[700],
                                        transition: 'all 0.2s ease',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.backgroundColor = colors.gray[50];
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.backgroundColor = 'white';
                                    }}
                                >
                                    <span>🚪</span>
                                    Logout
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Feedback Modal */}
            <FeedbackModal 
                isOpen={showFeedbackModal} 
                onClose={() => setShowFeedbackModal(false)} 
            />
        </div>
    );
}

export default TopBar;